import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toastError } from '../../utils/toast';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import useConfirmModal from '../ConfirmModal/useConfirmModal';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import Step4Photos from '../../pages/OnboardingWizard/Step4Photos';
import { WizardContext } from '../../pages/OnboardingWizard/WizardContext';
import './PhotosEditorModal.css';

function normalizeCandidatePhoto(candidate) {
    if (typeof candidate === 'string') {
        return { url: candidate };
    }
    return candidate && typeof candidate === 'object' ? candidate : null;
}

/** Merge classification from enrichment candidatePhotos — listing.photos schema omits category/extras. */
function seedPhotosFromListing(listing) {
    const candidateByUrl = new Map();
    for (const candidate of listing.enrichmentFields?.candidatePhotos ?? []) {
        const norm = normalizeCandidatePhoto(candidate);
        if (norm?.url) candidateByUrl.set(norm.url, norm);
    }

    return Array.isArray(listing.photos)
        ? listing.photos.map((photo) => {
            const match = candidateByUrl.get(photo.url);
            return {
                ...photo,
                category: photo.category ?? match?.category ?? null,
                quality: photo.quality ?? match?.quality ?? null,
                extras: photo.extras ?? match?.extras ?? [],
            };
        })
        : [];
}

function DrawerPhotosWizardProvider({ listing, children }) {
    const [photos, setPhotos] = useState(() => seedPhotosFromListing(listing));

    const noop = useCallback(() => {}, []);

    const value = useMemo(() => ({
        listingId: listing._id,
        setListingId: noop,
        addressData: listing.addressData ?? null,
        setAddressData: noop,
        hasAddress: true,
        confirmedFields: listing.confirmedFields ?? {},
        setConfirmedFields: noop,
        photos,
        setPhotos,
        geoSpaceCount: listing.geoSpaceCount ?? null,
        setGeoSpaceCount: noop,
        geofence: listing.geofence ?? null,
        setGeofence: noop,
        ingress: listing.ingress ?? [],
        setIngress: noop,
        egress: listing.egress ?? [],
        setEgress: noop,
        pricing: listing.pricing ?? { hourlyRate: null, maxSpots: null },
        setPricing: noop,
        managers: [],
        setManagers: noop,
        completedSteps: [],
        markStepComplete: noop,
        clearFromStep: noop,
        clearPricingStep: noop,
        resetWizard: noop,
    }), [listing, photos, noop]);

    return (
        <WizardContext.Provider value={value}>
            {children}
        </WizardContext.Provider>
    );
}

export default function PhotosEditorModal({
    open,
    onClose,
    listing,
    // PP-419 — photos are STAGED into the drawer's dirtyFields instead of
    // PUTting immediately. Save Changes on the drawer commits the whole set
    // (photos + pricing + spots + instructions) atomically. Cloudflare uploads
    // still happen at modal-save time (that's how the URLs are minted), but
    // no listing PUT until the drawer save fires; images sitting in Cloudflare
    // without a listing reference are acceptable orphans.
    onStagePhotos,
}) {
    const saveRef = useRef(null);
    const modalRef = useRef(null);
    const confirmModal = useConfirmModal();
    const [saving, setSaving] = useState(false);
    const initialPhotosJson = useRef(JSON.stringify(listing.photos ?? []));

    const isDirty = useCallback(() => {
        if (saveRef.current?.isDirty) return saveRef.current.isDirty();
        return false;
    }, []);

    const requestClose = useCallback(() => {
        if (isDirty()) {
            confirmModal.confirm({
                title: 'Discard photo changes?',
                message: 'Your photo edits will be lost.',
                confirmLabel: 'Discard',
                cancelLabel: 'Keep editing',
                variant: 'danger',
                onConfirm: onClose,
            });
            return;
        }
        onClose();
    }, [confirmModal, isDirty, onClose]);

    useFocusTrap(modalRef, open, requestClose);

    const handleSave = async () => {
        if (!saveRef.current?.save || saving) return;
        setSaving(true);
        try {
            const merged = await saveRef.current.save();
            if (!merged) return;
            // Block save when the host has removed every photo. The server
            // still rejects this for live listings too — see PP-417/PP-419 —
            // but a client-side gate here surfaces a cleaner message than
            // waiting for the drawer's Save Changes to hit the 422.
            if (!Array.isArray(merged) || merged.length === 0) {
                toastError('Add at least one photo before saving.');
                return;
            }
            // PP-419 — stage into the drawer's dirtyFields; the drawer's
            // Save Changes button is what actually PUTs to the server.
            onStagePhotos?.(merged);
            onClose();
        } catch {
            toastError('Could not save photos');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <>
            <div className="photos-editor-modal-root">
                <div
                    className="photos-editor-modal-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) requestClose();
                    }}
                >
                    <div
                        ref={modalRef}
                        className="photos-editor-modal-panel"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="photos-editor-title"
                    >
                        <header className="photos-editor-modal-head">
                            <h3 id="photos-editor-title">Add and verify photos</h3>
                            <button
                                type="button"
                                className="photos-editor-modal-close"
                                onClick={requestClose}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </header>
                        <div className="photos-editor-modal-body">
                            <DrawerPhotosWizardProvider listing={listing} key={initialPhotosJson.current}>
                                <Step4Photos embedded saveRef={saveRef} />
                            </DrawerPhotosWizardProvider>
                        </div>
                        <footer className="photos-editor-modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={requestClose}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving…' : 'Save changes'}
                            </button>
                        </footer>
                    </div>
                </div>
            </div>
            <ConfirmModal {...confirmModal.props} />
        </>,
        document.body,
    );
}
